import { useState } from 'react';

import type { MessageImage, MessageView, SharedCard } from '../../api/chat.types';
import { mediaUrl } from '../../api/client';
import { Avatar } from '../social/Avatar';
import { clockTime } from '../social/format';
import { splitLinks } from '../social/links';

/// Khung ảnh trong bubble: rộng tối đa 260, cao tối đa 320, giữ tỉ lệ thật để không nhảy layout khi ảnh tải xong.
const IMAGE_MAX_W = 260;
const IMAGE_MAX_H = 320;

function ImageBubble({ image, mine }: { image: MessageImage; mine: boolean }) {
  const scale = Math.min(1, IMAGE_MAX_W / image.width, IMAGE_MAX_H / image.height);
  const width = Math.max(80, Math.round(image.width * scale));
  const height = Math.max(60, Math.round(image.height * scale));
  const src = mediaUrl(image.path);
  return (
    // Bấm mở ảnh gốc ở tab mới; noopener để trang ảnh không với ngược lại được tab chat.
    <a href={src} target="_blank" rel="noopener noreferrer" aria-label="Mở ảnh" style={{ display: 'block', lineHeight: 0 }}>
      <img
        src={src}
        alt="Ảnh"
        loading="lazy"
        width={width}
        height={height}
        style={{
          width,
          height,
          objectFit: 'cover',
          borderRadius: 18,
          borderBottomRightRadius: mine ? 6 : 18,
          borderBottomLeftRadius: mine ? 18 : 6,
          background: 'var(--surface-tint)',
          border: '1px solid var(--border-default)',
        }}
      />
    </a>
  );
}

/** Chữ tin nhắn với link bấm được — mở tab mới, không gửi referrer. */
function LinkedText({ text, mine }: { text: string; mine: boolean }) {
  return (
    <>
      {splitLinks(text).map((part, i) =>
        part.href ? (
          <a
            key={i}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            style={{ color: mine ? '#fff' : 'var(--link)', textDecoration: 'underline', textUnderlineOffset: 2 }}
          >
            {part.text}
          </a>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

function SharedCardBubble({ card }: { card: SharedCard }) {
  return (
    <div
      style={{
        minWidth: 200,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'center',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-page)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{card.kind}</span>
      <span style={{ fontFamily: 'var(--font-jp)', fontSize: 40, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.3 }}>{card.jp}</span>
      <span style={{ fontSize: 14, color: 'var(--ink)' }}>{card.vi}</span>
    </div>
  );
}

/** Một tin. Nhóm: avatar + tên người gửi chỉ ở tin đầu mỗi cụm (`showSender`), các tin sau chừa lề bằng avatar. */
export function ChatBubble({ message, mine, group, showSender }: { message: MessageView; mine: boolean; group: boolean; showSender: boolean }) {
  const [translated, setTranslated] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%', flexDirection: mine ? 'row-reverse' : 'row', alignItems: 'flex-end' }}>
      {!mine && group && <div style={{ width: 30, flexShrink: 0 }}>{showSender && <Avatar name={message.senderName} size={30} />}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: mine ? 'flex-end' : 'flex-start', minWidth: 0 }}>
        {!mine && group && showSender && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', paddingLeft: 4 }}>{message.senderName}</span>}
        {message.image ? (
          <ImageBubble image={message.image} mine={mine} />
        ) : message.sharedCard ? (
          <SharedCardBubble card={message.sharedCard} />
        ) : (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 18,
              borderBottomRightRadius: mine ? 6 : 18,
              borderBottomLeftRadius: mine ? 18 : 6,
              background: mine ? 'var(--cta-default)' : 'var(--surface-tint)',
              color: mine ? '#fff' : 'var(--ink)',
              fontSize: 15,
              lineHeight: 1.45,
              fontFamily: message.isJapanese ? 'var(--font-jp)' : 'var(--font-vi)',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            <LinkedText text={message.text} mine={mine} />
            {message.isJapanese && translated && (
              <div
                style={{
                  marginTop: 6,
                  paddingTop: 6,
                  borderTop: '1px solid var(--border-default)',
                  fontFamily: 'var(--font-vi)',
                  fontSize: 13,
                  color: mine ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {message.translation ?? 'Chưa có bản dịch cho tin này.'}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', padding: '0 4px' }}>
          <span>{clockTime(message.createdAt)}</span>
          {message.isJapanese && (
            <button
              type="button"
              onClick={() => setTranslated((v) => !v)}
              // Chữ 12px, vùng bấm cao 44px nhờ padding dọc + margin âm.
              style={{ border: 'none', background: 'none', padding: '14px 4px', margin: '-14px -4px', cursor: 'pointer', fontFamily: 'var(--font-vi)', fontSize: 12, fontWeight: 700, color: 'var(--link)' }}
            >
              {translated ? 'Ẩn dịch' : 'Dịch'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
